import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Check, Loader2 } from "lucide-react";
import { TID } from "../lib/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Brand Activation",
  "Sponsorship Integration",
  "Product Launch",
  "Content & Visual Production",
  "Music / Festival",
  "Corporate Event",
  "Bespoke Experience",
  "Artist Management",
];

const BUDGETS = ["< ₹10L", "₹10L – ₹50L", "₹50L – ₹2Cr", "₹2Cr+"];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    service: "",
    budget: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const pick = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setError("Name, email and a message are required.");
      setStatus("error");
      return;
    }
    setStatus("loading");
    setError("");
    try {
      await axios.post(`${API}/inquiries`, form);
      setStatus("success");
      setForm({
        name: "",
        email: "",
        company: "",
        service: "",
        budget: "",
        message: "",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      setError(typeof msg === "string" ? msg : "Please check your inputs.");
      setStatus("error");
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-32 border-t border-white/10">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-12 gap-8 md:gap-12">
          <div className="col-span-12 md:col-span-5">
            <span className="em-tag">07 — Contact</span>
            <h2
              className="mt-6 font-display font-extrabold tracking-tighter leading-[0.98]"
              style={{ fontSize: "clamp(2rem, 4.6vw, 4.4rem)" }}
            >
              Tell us about the moment.
            </h2>
            <p className="mt-6 text-white/70 leading-relaxed max-w-md">
              Have a launch, a festival, a brand chapter in your head?
              Send it over — even in fragments. We reply within two working days.
            </p>

            <div className="mt-14 space-y-6">
              <div>
                <div className="font-mono-em text-[0.7rem] tracking-[0.24em] uppercase text-white/50">
                  Studio
                </div>
                <div className="mt-2 text-white/85 leading-snug">
                  Eventmonkpro Pvt. Ltd.
                  <br />
                  Suite 805, Level 2 — KC Arcade,
                  <br />
                  Thuthiyoor Road, Kakkanad,
                  <br />
                  Kochi, India — 682 030
                </div>
              </div>
              <div>
                <div className="font-mono-em text-[0.7rem] tracking-[0.24em] uppercase text-white/50">
                  Direct
                </div>
                <a
                  href="mailto:support@eventmonk.in"
                  className="mt-2 block em-underline-link"
                >
                  support@eventmonk.in
                </a>
                <a
                  href="tel:+919562451105"
                  className="mt-1 block em-underline-link"
                >
                  +91 95624 51105
                </a>
              </div>
            </div>
          </div>

          <form
            onSubmit={submit}
            data-testid={TID.contactForm}
            className="col-span-12 md:col-span-7 md:pl-8 border-t md:border-t-0 md:border-l border-white/10 pt-10 md:pt-0 md:pl-12 relative"
          >
            <AnimatePresence mode="wait">
              {status === "success" ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                  data-testid={TID.contactSuccess}
                  className="min-h-[400px] flex flex-col justify-center"
                >
                  <div className="h-14 w-14 rounded-full grid place-items-center border border-white/20">
                    <Check size={22} />
                  </div>
                  <h3
                    className="mt-6 font-display font-extrabold tracking-tighter leading-none"
                    style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)" }}
                  >
                    Thank you.
                  </h3>
                  <p className="mt-4 text-white/70 max-w-md">
                    Your brief just landed in our studio inbox. Expect a reply from a real
                    human within two working days.
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus("idle")}
                    className="em-btn em-btn-ghost border-white/20 mt-8 self-start"
                  >
                    Send another
                  </button>
                </motion.div>
              ) : (
                <motion.div
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
                    <label className="block">
                      <span className="font-mono-em text-[0.68rem] tracking-[0.24em] uppercase text-white/50">
                        01 — Your name
                      </span>
                      <input
                        data-testid={TID.contactName}
                        className="em-input mt-2"
                        placeholder="Full name"
                        value={form.name}
                        onChange={update("name")}
                        required
                      />
                    </label>
                    <label className="block">
                      <span className="font-mono-em text-[0.68rem] tracking-[0.24em] uppercase text-white/50">
                        02 — Email
                      </span>
                      <input
                        data-testid={TID.contactEmail}
                        type="email"
                        className="em-input mt-2"
                        placeholder="you@brand.com"
                        value={form.email}
                        onChange={update("email")}
                        required
                      />
                    </label>
                    <label className="block md:col-span-2">
                      <span className="font-mono-em text-[0.68rem] tracking-[0.24em] uppercase text-white/50">
                        03 — Company (optional)
                      </span>
                      <input
                        data-testid={TID.contactCompany}
                        className="em-input mt-2"
                        placeholder="Brand / organisation"
                        value={form.company}
                        onChange={update("company")}
                      />
                    </label>
                  </div>

                  <div className="mt-10">
                    <span className="font-mono-em text-[0.68rem] tracking-[0.24em] uppercase text-white/50">
                      04 — I'm looking for
                    </span>
                    <div
                      data-testid={TID.contactService}
                      className="mt-4 flex flex-wrap gap-2"
                    >
                      {SERVICES.map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => pick("service", s)}
                          className={`px-4 py-2 rounded-full border text-sm transition-[background-color,border-color,color] duration-400 ${
                            form.service === s
                              ? "bg-white text-black border-white"
                              : "bg-transparent text-white/80 border-white/15 hover:border-white/40"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mt-10">
                    <span className="font-mono-em text-[0.68rem] tracking-[0.24em] uppercase text-white/50">
                      05 — Budget range
                    </span>
                    <div
                      data-testid={TID.contactBudget}
                      className="mt-4 flex flex-wrap gap-2"
                    >
                      {BUDGETS.map((b) => (
                        <button
                          key={b}
                          type="button"
                          onClick={() => pick("budget", b)}
                          className={`px-4 py-2 rounded-full border text-sm transition-[background-color,border-color,color] duration-400 ${
                            form.budget === b
                              ? "bg-white text-black border-white"
                              : "bg-transparent text-white/80 border-white/15 hover:border-white/40"
                          }`}
                        >
                          {b}
                        </button>
                      ))}
                    </div>
                  </div>

                  <label className="block mt-10">
                    <span className="font-mono-em text-[0.68rem] tracking-[0.24em] uppercase text-white/50">
                      06 — The brief
                    </span>
                    <textarea
                      data-testid={TID.contactMessage}
                      className="em-input mt-2 resize-none"
                      rows={4}
                      placeholder="Tell us about the moment, the audience, the date…"
                      value={form.message}
                      onChange={update("message")}
                      required
                    />
                  </label>

                  {status === "error" && (
                    <p className="mt-6 text-sm text-[#FF4D66]">{error}</p>
                  )}

                  <div className="mt-10 flex items-center justify-between gap-6 flex-wrap">
                    <p className="text-white/50 text-sm max-w-sm">
                      By sending this, you agree we may contact you about your enquiry. That's it.
                    </p>
                    <button
                      data-testid={TID.contactSubmit}
                      type="submit"
                      disabled={status === "loading"}
                      className="em-btn em-btn-primary disabled:opacity-70"
                    >
                      {status === "loading" ? (
                        <>
                          <Loader2 size={16} className="animate-spin" /> Sending…
                        </>
                      ) : (
                        <>
                          Send Brief <ArrowUpRight size={16} />
                        </>
                      )}
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </form>
        </div>
      </div>
    </section>
  );
}
